import { useEffect, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { dateToString, notNullToDate, toFloat, toInt } from "../../utilities/auxiliary-functions";
import { removeFormatDate } from "../../utilities/masks";
import { isEmpty } from "../../utilities/validators";
import DeliveryRecord from "../records/delivery.record";
import { FaTimes } from "react-icons/fa";
import { useAlertMessageContext } from "../../contexts/alert-message.context";
import { useConfirmMessageContext } from "../../contexts/confirm-message.context";
import { AlertMessageEnum, EDeliveryStatus } from "../../utilities/types";
import { Button } from '@mui/material';
import cduService from "../../services/cdu.service";
import { useTranslateContext } from "../../contexts/translate.context";
import { usePartnerContext } from "../../contexts/partner.context";
import dlvService from "../../services/dlv.service";
import lclService from "../../services/lcl.service";
import srvService from "../../services/srv.service";
import { TFilter } from "../../types/FilterType";
import authService from "../../services/auth.service";

export interface IDeliveryCadastreModalProps {
    isEdit: boolean,
    show: boolean,
    recordId: string,
    record: any,
    records: any,
    onSubmitModal: any,
    onCancelModal: any
}

export enum ENormApplicant {
    VESSEL = 0,
    MEMBER = 1,
    EMPLOYEE = 2,
    OTHER = 3
}

export const DeliveryCadastreModal: React.FC<IDeliveryCadastreModalProps> = (props: IDeliveryCadastreModalProps) => {

    const alertContext = useAlertMessageContext();
    const confirmContext = useConfirmMessageContext();
    const { display } = useTranslateContext();
    let { partner } = usePartnerContext();

    const title : string = display.title.delivery;

    const [ invalidatedCadastreForm, setInvalidatedCadastreForm ] = useState(false);
    const [ sending, setSending ] = useState(false);

    // Delivery
    const [ id, setId ] = useState(0);
    const [ launch, setLaunch ] = useState(dateToString(new Date(), 'dd/mm/yyyy HH:MM:ss')); 
    const [ applicant, setApplicant ] = useState(''); 
    const [ applicantId, setApplicantId ] = useState(''); 
    const [ status, setStatus ] = useState(EDeliveryStatus.REQUESTED); 
    const [ payment, setPayment ] = useState(''); 
    const [ operator, setOperator ] = useState('');
    const [ quantity, setQuantity ] = useState(0);
    const [ amount, setAmount ] = useState(0);
    const [ deliverItems, setDeliverItems ] = useState<TDeliveryItem[]>([] as any);
    const [ created, setCreated ] = useState('');
    const [ updated, setUpdated ] = useState('');

    const [ normApplicant, setNormApplicant ] = useState(ENormApplicant.VESSEL);
    const [ listApplicant, setListApplicant ] = useState([] as any);
    const [ group, setGroup ] = useState('');
    const [ listGroup, setListGroup ] = useState([] as any);
    const [ subgroup, setSubgroup ] = useState('');
    const [ listSubgroup, setListSubgroup ] = useState([] as any);

    type TDeliveryItem = {
        id: number,
        productId: any;
        stock?: any,
        quantity: number,
        amount: number,
        observation: string
    };
    let [ listFilteredItem, setListFilteredItem ] = useState<TDeliveryItem[]>([] as any);
    let [ listOriginalItem, setListOriginalItem ] = useState<TDeliveryItem[]>([] as any);

    async function isValidForm() {
        let result = true;
        let emptyRequired = false;

        if ((toInt(normApplicant) === ENormApplicant.VESSEL) ||
            (toInt(normApplicant) === ENormApplicant.MEMBER) ||
            (toInt(normApplicant) === ENormApplicant.EMPLOYEE)) {
            if (isEmpty(applicantId))
                emptyRequired = true;
        } else {
            if (isEmpty(applicant))
                emptyRequired = true;
        }

        if (emptyRequired) {
            await alertContext.show(AlertMessageEnum.FAIL, title, display.message.invalid.required);
            result = false;
        }

        setInvalidatedCadastreForm(!result);
        return result;
    }

    async function onClick_Confirm(event : any) {
        event.preventDefault();

        const isValid = await isValidForm();
        if (isValid) {
            try {
                const isConfirmed = await confirmContext.show(title, display.message.confirm_record);
                if (isConfirmed) {
                    setSending(false);
                    const delivery = fillDelivery();
                    if (delivery.id > 0)
                        await dlvService.saveDeliver(delivery.id, delivery);
                    else
                        await dlvService.createDeliver(delivery);

                    setSending(false);
                    await alertContext.show(AlertMessageEnum.SUCCESS, title, display.message.the_record_has_been_saved_successfully);
                    props.onSubmitModal(event, delivery);
                }
            } catch (error: any) {
                setSending(false);
                await alertContext.show(AlertMessageEnum.FAIL, title, error);
            }
        }
    }

    async function onClick_Cancel(event : any) {
        event.preventDefault();

        setInvalidatedCadastreForm(false);
        props.onCancelModal(event);
    }

    function clearDelivery() {
        setId(0);
        setLaunch(dateToString(new Date(), 'dd/mm/yyyy HH:MM:ss'));
        setApplicant('');
        setApplicantId('');
        setStatus(EDeliveryStatus.REQUESTED);
        setPayment('');
        setOperator('');
        setQuantity(0);
        setAmount(0);
        setDeliverItems([] as any);
        setCreated('');
        setUpdated('');

        setNormApplicant(ENormApplicant.VESSEL);
    }

    function fillDataForm(deliver: any) {

        if (deliver != null) {
            setId(deliver.id);
            setLaunch(notNullToDate(deliver.launch, 'dd/mm/yyyy HH:MM:ss'));
            setApplicant(deliver.applicant);
            setApplicantId(deliver.applicantId);
            setStatus(deliver.status);
            setPayment(deliver.payment);
            setOperator(deliver.operator);
            setQuantity(deliver.quantity);
            setAmount(deliver.amount);
            setDeliverItems(deliver.deliverItems);
            setCreated(notNullToDate(deliver.created, 'dd/mm/yyyy HH:MM:ss'));
            setUpdated(notNullToDate(deliver.updated, 'dd/mm/yyyy HH:MM:ss'));

            if (deliver.applicant === 'VESSEL')
                setNormApplicant(ENormApplicant.VESSEL);
            else if (deliver.applicant === 'MEMBER')
                setNormApplicant(ENormApplicant.MEMBER);
            else if (deliver.applicant === 'EMPLOYEE')
                setNormApplicant(ENormApplicant.EMPLOYEE);
            else
                setNormApplicant(ENormApplicant.OTHER);
        }
    }

    function fillDelivery() {
        const user = authService.currentUser();

        const dataDeliverItems = [] as any;
        listOriginalItem.forEach((item: any) => {
            if (item.quantity > 0) {
                dataDeliverItems.push({
                    id: item.id,
                    productId: item.productId,
                    quantity: item.quantity,
                    amount: item.amount,
                    observation: item.observation
                });
            }
        });
        
        const dataDelivery = {
            id,
            partnerId: partner.id,
            launch: removeFormatDate(launch),
            applicant,
            applicantId,
            status: String(status),
            payment,
            operator: user.username,
            quantity,
            amount,
            deliverItems: dataDeliverItems,
            created: removeFormatDate(created), 
            updated: removeFormatDate(updated)
        };

        return dataDelivery;
    }

    async function mountGroup() {
        // Material  = 5 - Bistrô e Restaurante
        const filter = {
            where: { material: 4, team: 2 }
        } as TFilter;
        let categoryFilter = await cduService.categoryFilter(filter);

        let groups = [] as any;
        for (let i = 0; i < categoryFilter.rows.length; i++) {
            let obj = categoryFilter.rows[i];
            const exist = groups.find((item: any) => item.group === obj.group);
            if ((!exist) && !isEmpty(obj.group))
                groups.push(obj);
        }
        setSubgroup('');
        groups = groups.sort(function (a: any, b: any) {
            return a.group.localeCompare(b.group);
        });
        setListGroup(groups);
    }

    async function mountProduct(items: any) {
        let deliveryItems : TDeliveryItem[] = [];
        // Material  = 5 - Bistrô e Restaurante
        const filter = {
            include: [
                { all: true},
                {
                    model: "Product",
                    include: [{
                        model: "Category"
                    }]
                }
            ],
            where: { 'category.material': 4 }
        } as TFilter;
        const stockFilter = await srvService.stockFilter(filter);
        for (let i = 0; i < stockFilter.rows.length; i++) {
            let item = stockFilter.rows[i];
            deliveryItems.push({ 
                id: 0,
                productId: item.productId,
                stock: item,
                quantity: 0,
                amount: 0,
                observation: ''
            });
        }

        for ( let i = 0; i < items.length; i++ ) {
            const item = items[i];
            const product = deliveryItems.find((deliver: any) => deliver.productId === item.productId );
            if (product) {
                product.id = item.id;
                product.quantity = item.quantity;
                product.amount = item.amount;
                product.observation = item.observation;
            }
        }

        setListOriginalItem(deliveryItems);
        setListFilteredItem(deliveryItems);
    }

    async function initialize() {
        let deliverItems = [] as any;
        if (props.isEdit) {
            const attributes: string[] = ['id', 'productId', 'quantity', 'amount', 'observation'];
            const resultItems = await dlvService.itemList(props.record.id, attributes);
            deliverItems = resultItems.rows;
            props.record['deliveryItems'] = deliverItems;
            fillDataForm(props.record);
        } else {
            clearDelivery();
        }
        await mountProduct(deliverItems);
        await mountGroup();
        await onChangeNormApplication(null);
    }

    async function onChangeNormApplication(event: any) {
        const list = [] as any;
        let result = {} as any;
        
        let norm = normApplicant;
        if (event)
            norm = toInt(event.target.value);

        let applicantName = '';
        try {
            if (toInt(norm) === ENormApplicant.VESSEL) {
                result = await lclService.vesselList(partner.id);
                result.rows.forEach((obj: any) => {
                    list.push({ id: obj.id, name: obj.name });
                });
                applicantName = 'VESSEL';
            } else if (toInt(norm) === ENormApplicant.MEMBER) {
                result = await lclService.memberList(partner.id);
                result.rows.forEach((obj: any) => {
                    list.push({ id: obj.id, name: obj.people.fullname });
                });
                applicantName = 'MEMBER';
            } else if (toInt(norm) === ENormApplicant.EMPLOYEE) {
                result = await cduService.employeeList(partner.company.id);
                result.rows.forEach((obj: any) => {
                    list.push({ id: obj.id, name: obj.people.fullname });
                });
                applicantName = 'EMPLOYEE';
            } else {
                applicantName = '';
            }
        } catch (error: any) {
            await alertContext.show(AlertMessageEnum.FAIL, title, error);
        }
        setListApplicant(list);
        if (event) {
            setApplicant(applicantName);
            setApplicantId('');
        }
    }
    
    async function onChangeGroup(event: any) {
        let group = event.target.value;
        // Material  = 5 - Bistrô e Restaurante
        const filter = {
            where: { material: 4, team: 2, group }
        } as TFilter;
        let categoryFilter = await cduService.categoryFilter(filter);

        let subgroups = [] as any;
        for (let i = 0; i < categoryFilter.rows.length; i++) {
            let obj = categoryFilter.rows[i];
            const exist = subgroups.find((item: any) => item.group === obj.group && item.subgroup === obj.subgroup);
            if ((!exist) && !isEmpty(obj.subgroup))
                subgroups.push(obj);
        }
        subgroups = subgroups.sort(function (a: any, b: any) {
            return a.subgroup.localeCompare(b.subgroup);
        });
        setListSubgroup(subgroups);
    }

    function onChangeProducts(event: any, group?: string, subgroup?: any) {
        event.preventDefault();
        let deliveryItems = listOriginalItem;

        if (!isEmpty(group)) {
            let listFiltered = [] as any;
            if (isEmpty(subgroup))
                listFiltered = deliveryItems.filter((item: any) => item.stock.product.category.group === group );
            else
                listFiltered = deliveryItems.filter((item: any) => item.stock.product.category.group === group && item.stock.product.category.subgroup === subgroup );
            setListFilteredItem(listFiltered);
        } else {
            setListFilteredItem(deliveryItems);
        }
    }

    function onChangeProductItem(event: any, position: number, productItem: any) {
        event.preventDefault();

        let qty = toInt(productItem.quantity);
        let amt = qty * toFloat(productItem.stock.sale);
        productItem = {...productItem, amount: amt};

        const list = listFilteredItem.map((item: any, index: number) => {
            if (index === position)
                return productItem;
            return item;
        });
        setListFilteredItem(list);

        for ( let i = 0; i < list.length; i++ ) {
            const item = list[i];
            const product = listOriginalItem.find((deliver: any) => deliver.productId === item.productId );
            if (product) {
                product.id = item.id;
                product.quantity = item.quantity;
                product.amount = item.amount;
                product.observation = item.observation;
            }
        }

        let quantity = 0;
        let amount = 0;
        listOriginalItem.forEach((item: any) => {
            quantity += toInt(item.quantity);
            amount += toFloat(item.amount);
        });
        setQuantity(quantity);
        setAmount(amount);
    }
    
    let reloadPage = false;
    useEffect(() => {
        if (!reloadPage) {
            if (isEmpty(partner))
                // eslint-disable-next-line react-hooks/exhaustive-deps
                partner = authService.currentPartner();

            if (props.show)
                initialize();
        }

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            reloadPage = !reloadPage;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show]);

    function viewCadastreForm() {
        return (
            <>
                <Modal show={props.show}
                    size="lg"
                    aria-labelledby="modal-cadastre-delivery-form"
                    centered
                >
                    <Modal.Header>
                        <Modal.Title id="modal-cadastre-delivery-form">{title}</Modal.Title>
                        <FaTimes className="isClickable" onClick={ onClick_Cancel } size={26}/>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="form" id="form-cadastre-delivery" validated={invalidatedCadastreForm}>
                            <DeliveryRecord
                                title={title}
                                isEdit={true}
                                identifier={id === 0}

                                normApplicant={normApplicant}
                                setNormApplicant={setNormApplicant}
                                listApplicant={listApplicant}

                                listGroup={listGroup}
                                listSubgroup={listSubgroup}
                                listProductItem={listFilteredItem}
                            
                                group={group}
                                subgroup={subgroup}
                                setGroup={setGroup}
                                setSubgroup={setSubgroup}
                                
                                created={created}
                                updated={updated}
                        
                                launch={launch}
                                applicant={applicant}
                                applicantId={applicantId}
                                status={status}
                                payment={payment}
                                operator={operator}
                                amount={amount}
                                quantity={quantity}
                                setLaunch={(value: any) => { setLaunch(value); }}
                                setApplicant={(value: any) => { setApplicant(value); }}
                                setApplicantId={(value: any) => { setApplicantId(value); }}
                                setStatus={(value: any) => { setStatus(value); }}
                                setPayment={(value: any) => { setPayment(value); }}
                                setOperator={(value: any) => { setOperator(value); }}
                                setAmount={(value: any) => { setAmount(value); }}
                                setQuantity={(value: any) => { setQuantity(value); }}

                                onChangeNormApplication={onChangeNormApplication}
                                onChangeGroup={onChangeGroup}
                                onChangeProducts={onChangeProducts}
                                onChangeProductItem={onChangeProductItem}
                            />
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={ onClick_Cancel } variant="contained" color="secondary">{display.buttom.cancel}</Button>
                        <Button  variant="contained" color="primary" onClick={ onClick_Confirm } disabled={sending}>
                            { sending ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : null }{'  '}
                            { display.buttom.confirm }
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        )
    }

    return (
        <>
        { viewCadastreForm() }
        </>
    )
}

export default DeliveryCadastreModal
